/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/adminEmailSequence.scss';

import React, {MouseEventHandler} from 'react';
import {capitalize, Checkbox, CloseButton, Loader, voidFunction} from "@dvrd/dvr-controls";
import IDate from '@dvrd/idate';
import EmailStep from "../../../models/emailStepModel";
import {BolmateButton, DvrdDatePicker} from "../../controls";
import {
    getAttachmentsLabel,
    getCountriesLabel,
    getCustomersLabel,
    getLogisticsLabel,
    getProductsLabel, getStepTitle
} from "../../../util/emailStepUtil";
import classNames from 'classnames';

interface Props {
    onSelectEmailStep: (emailStepId: string) => MouseEventHandler;
    onChangeFromDate: (value: IDate | null) => void;
    onChangeToDate: (value: IDate | null) => void;
    onSubmit: MouseEventHandler;
    emailSteps: Array<EmailStep>;
    selectedEmailSteps: Set<string>;
    fromDate: IDate | null;
    toDate: IDate | null;
    loading: boolean;
    loadingEmailSteps: boolean;
    loadingInvoiceCount: boolean;
    invoiceCount: number;
    productsCount: number;
}

export default function AdminEmailSequence(props: Props) {
    const {
        emailSteps, loadingEmailSteps, onSelectEmailStep, loading, selectedEmailSteps, fromDate, onChangeFromDate,
        toDate, onChangeToDate, onSubmit, invoiceCount, loadingInvoiceCount, productsCount
    } = props;

    function onClearFromDate() {
        onChangeFromDate(null);
    }

    function onClearToDate() {
        onChangeToDate(null);
    }

    function renderEmailSteps() {
        return (
            <div className='email-steps'>
                <Loader active={loadingEmailSteps}/>
                <label className='section-title np'>Selecteer welke e-mails verzonden moeten worden</label>
                {emailSteps.map(renderEmailStep)}
            </div>
        )
    }

    function renderEmailStep(emailStep: EmailStep, index: number) {
        const selected = selectedEmailSteps.has(emailStep.id);
        const stepTitle = capitalize(getStepTitle(emailStep).toLowerCase());
        const {active} = emailStep;
        return (
            <div className={classNames('admin-email-step', !active && 'disabled')} key={index}
                 onClick={active ? onSelectEmailStep(emailStep.id) : undefined}>
                <Checkbox onCheck={voidFunction} checked={selected} disabled={!active}/>
                <label className='email-step-label'><span
                    className='bold text'>{stepTitle}:</span> Na <span
                    className='bold'>{emailStep.translatedDays}</span> voor <span
                    className='bold'>{getProductsLabel(emailStep, productsCount)}</span> stuur <span
                    className='bold'>{emailStep.template?.name ?? 'Geen template'}</span> mail met
                    bijlage <span
                        className='bold'>{getAttachmentsLabel(emailStep)}</span> naar <span
                        className='bold'>{getCustomersLabel(emailStep)}</span> in <span
                        className='bold'>{getCountriesLabel(emailStep)}</span> via <span
                        className='bold'>{getLogisticsLabel(emailStep)}</span>
                </label>
            </div>
        )
    }

    function renderInvoiceCount() {
        if (loadingInvoiceCount) return <label className='invoice-count'>Aantal facturen bepalen...</label>;
        const countLabel = invoiceCount === 1 ? 'factuur' : 'facturen';
        return <label className='invoice-count'>{invoiceCount} {countLabel} geselecteerd</label>;
    }

    return (
        <section className='admin-email-sequence'>
            <label className='section-title'>Instellingen</label>
            <div className='settings'>
                <div className='date-range'>
                    <div className='date-picker'>
                        <DvrdDatePicker onChange={onChangeFromDate} value={fromDate} label='Orders vanaf'
                                        placeholder='dd-mm-jjjj' dateFormat='DD-MM-YYYY' closeOnChange/>
                        {!!fromDate && <CloseButton onClick={onClearFromDate} className='clear-btn'/>}
                    </div>
                    <div className='date-picker'>
                        <DvrdDatePicker onChange={onChangeToDate} value={toDate} label='Orders tot'
                                        placeholder='dd-mm-jjjj'
                                        dateFormat='DD-MM-YYYY' closeOnChange/>
                        {!!toDate && <CloseButton onClick={onClearToDate} className='clear-btn'/>}
                    </div>
                    {renderInvoiceCount()}
                </div>
                {renderEmailSteps()}
                <BolmateButton onClick={onSubmit} label='Start' disabled={loading || loadingEmailSteps}/>
            </div>
        </section>
    )
}